import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  BookOpen,
  GraduationCap,
  Award,
  Trophy,
  Map,
  MessageSquare,
  Settings,
  Users,
  BarChart3,
  LogOut,
  Menu,
  X,
  Search,
  Bell,
  Database,
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['all'] },
  { path: '/courses', label: 'Explore', icon: BookOpen, roles: ['all'] },
  { path: '/my-learning', label: 'My Learning', icon: GraduationCap, roles: ['all'] },
  { path: '/certificates', label: 'Certificates', icon: Award, roles: ['all'] },
  { path: '/leaderboard', label: 'Leaderboard', icon: Trophy, roles: ['all'] },
  { path: '/learning-paths', label: 'Career Paths', icon: Map, roles: ['all'] },
  { path: '/forums', label: 'Community', icon: MessageSquare, roles: ['all'] },
];

const adminItems = [
  { path: '/admin/catalog', label: 'Catalog Manager', icon: Database, roles: ['super_admin', 'hr_admin'] },
  { path: '/admin/courses', label: 'Manage Courses', icon: Settings, roles: ['super_admin', 'hr_admin'] },
  { path: '/admin/users', label: 'Manage Users', icon: Users, roles: ['super_admin', 'hr_admin'] },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3, roles: ['super_admin', 'hr_admin', 'manager'] },
];

export const Layout = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isActive = (path) => location.pathname === path;

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
          active 
            ? 'bg-blue-50 text-[#0056D2] border-l-4 border-[#0056D2] -ml-[4px] pl-[20px]' 
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`}
        onClick={() => setSidebarOpen(false)}
        data-testid={`nav-${item.path.replace(/\//g, '-')}`}
      >
        <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
        <span>{item.label}</span>
      </Link>
    );
  };

  const visibleAdminItems = adminItems.filter(
    (item) => item.roles.some((role) => hasRole(role))
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Top Navigation Bar - Coursera Style */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50">
        <div className="h-full max-w-[1800px] mx-auto px-4 flex items-center justify-between">
          {/* Left: Logo & Mobile Menu */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600"
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0056D2] rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-slate-900 hidden sm:block">SketchLearn</span>
            </Link>
          </div>

          {/* Center: Search Bar */}
          <div className="hidden md:flex flex-1 max-w-xl mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="text" 
                placeholder="What do you want to learn?"
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0056D2] focus:border-transparent"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Right: User Actions */}
          <div className="flex items-center gap-4">
            <button className="p-2 rounded-full hover:bg-slate-100 text-slate-600 hidden sm:flex">
              <Bell className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <Avatar className="w-9 h-9 ring-2 ring-slate-100">
                <AvatarFallback className="bg-[#0056D2] text-white text-sm font-semibold">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-slate-900 leading-tight">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-slate-200 z-40 overflow-y-auto ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 transition-transform duration-200`}
      >
        <div className="p-4">
          {/* Quick Stats */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">Your Progress</span>
              <span className="text-xs font-semibold text-[#0056D2] bg-white px-2 py-0.5 rounded-full">{user?.points || 0} XP</span>
            </div>
            <div className="h-2 bg-white/80 rounded-full overflow-hidden">
              <div className="h-full bg-[#0056D2] rounded-full" style={{ width: '45%' }}></div>
            </div>
            <p className="mt-2 text-xs text-slate-500">Keep learning to earn more points!</p>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {navItems.map(renderNavItem)}
          </nav>

          {/* Admin Section */}
          {visibleAdminItems.length > 0 && (
            <>
              <div className="mt-8 mb-3 px-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Administration
                </p>
              </div>
              <nav className="space-y-1">
                {visibleAdminItems.map(renderNavItem)}
              </nav>
            </>
          )}

          {/* Logout */}
          <div className="mt-8 pt-6 border-t border-slate-100">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-slate-600 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-30 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 min-h-screen">
        <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
